/**
 * Tag overflow detection, KMU tab switching, dropdown management.
 * Migrated from PRODUCTION tag-widget.js (287 lines).
 */

// ── Tag overflow detection ───────────────────────────────────────

function recalcTagOverflow(wrapper: HTMLElement): void {
  // KMU: on mobile (<= 1220px) tags scroll horizontally, no overflow hiding
  if (window.innerWidth <= 1220) return;
  if (!wrapper) return;
  requestAnimationFrame(() => {
    const container = wrapper.querySelector<HTMLElement>("[data-tags-container]");
    const dropdown = wrapper.querySelector<HTMLElement>("[data-tags-dropdown]");
    const dropdownItems = wrapper.querySelector<HTMLElement>("[data-tags-dropdown-items]");
    if (!container || !dropdown || !dropdownItems) return;

    const tags = Array.from(container.querySelectorAll<HTMLElement>(".js-count-tags"));

    // Reset: show all tags
    tags.forEach((t) => t.classList.remove("hidden"));
    dropdown.classList.remove("hidden");

    // Available width = parent width - dropdown width
    const availableWidth =
      container.parentElement!.getBoundingClientRect().width -
      dropdown.getBoundingClientRect().width;
    let usedWidth = dropdown.getBoundingClientRect().width;

    const overflowTags: HTMLElement[] = [];

    tags.forEach((tag) => {
      usedWidth += tag.getBoundingClientRect().width;
      if (usedWidth > availableWidth) {
        overflowTags.push(tag);
      }
    });

    // Rebuild dropdown menu with overflow tags
    dropdownItems.innerHTML = "";
    overflowTags.forEach((tag) => {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.classList.add("dropdown-item");
      btn.textContent = tag.textContent?.trim() ?? "";
      btn.setAttribute(
        "aria-label",
        "Відфільтрувати новини за темою " + btn.textContent,
      );
      const target = tag.getAttribute("data-bs-target");
      if (target) btn.setAttribute("data-bs-target", target);
      li.appendChild(btn);
      dropdownItems.appendChild(li);
      tag.classList.add("hidden");
      btn.addEventListener("click", () => tag.click());
    });

    if (overflowTags.length > 0) {
      dropdown.classList.remove("hidden");
    } else {
      dropdown.classList.add("hidden");
    }
  });
}

// ── KMU tab-pane switching ───────────────────────────────────────

function switchTagPane(button: Element): void {
  const targetSelector = button.getAttribute("data-bs-target");
  if (!targetSelector) return;

  const targetPane = document.querySelector(targetSelector);
  if (!targetPane) return;

  const tabContent = targetPane.parentElement;
  if (!tabContent) return;

  tabContent.querySelectorAll(".js-news-tab-content").forEach((pane) => {
    pane.classList.remove("active", "show");
  });

  targetPane.classList.add("active", "show");
}

function setActiveTag(container: Element, activeButton: Element): void {
  container
    .querySelectorAll(".wcag-tags-item, .dropdown-item")
    .forEach((el) => {
      el.classList.remove("active");
      el.setAttribute("aria-pressed", "false");
    });
  activeButton.classList.add("active");
  activeButton.setAttribute("aria-pressed", "true");
}

// ── Dropdown helpers ─────────────────────────────────────────────

function closeDropdown(dropdown: HTMLElement): void {
  const btn = dropdown.parentElement?.querySelector<HTMLElement>(".dropdown-button");
  dropdown.removeAttribute("style");
  btn?.setAttribute("aria-expanded", "false");
}

function closeAllDropdownsExcept(exceptDropdown: Element | null): void {
  document.querySelectorAll<HTMLElement>(".dropdown-menu").forEach((dd) => {
    if (dd === exceptDropdown) return;
    if (dd.offsetParent !== null || dd.style.display === "block") {
      closeDropdown(dd);
    }
  });
}

// ── Init ─────────────────────────────────────────────────────────

export function initTagWidget(): void {
  const allWrappers =
    document.querySelectorAll<HTMLElement>("[data-tags-wrapper]");
  allWrappers.forEach((w) => recalcTagOverflow(w));

  // Resize recalc
  let resizeTimer: ReturnType<typeof setTimeout>;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      // KMU: on <= 1220px show all tags, hide dropdown
      if (window.innerWidth <= 1220) {
        allWrappers.forEach((w) => {
          w.querySelectorAll<HTMLElement>(".js-count-tags").forEach((t) =>
            t.classList.remove("hidden"),
          );
          const dd = w.querySelector<HTMLElement>("[data-tags-dropdown]");
          if (dd) dd.classList.add("hidden");
        });
        return;
      }
      allWrappers.forEach((w) => recalcTagOverflow(w));
    }, 150);
  });

  // Tab switch recalc
  document.addEventListener("shown.bs.tab", () => {
    setTimeout(
      () => allWrappers.forEach((w) => recalcTagOverflow(w)),
      50,
    );
  });

  // Tag button clicks (main tags)
  document.addEventListener("click", (e) => {
    const tabBtn = (e.target as Element)?.closest<HTMLElement>(
      "[data-tags-container] .js_update_tabs",
    );
    if (!tabBtn || tabBtn.classList.contains("dropdown-item")) return;
    e.preventDefault();
    const container = tabBtn.closest("[data-tags-container]");
    if (!container) return;
    setActiveTag(container, tabBtn);
    switchTagPane(tabBtn);
  });

  // Dropdown item clicks
  document.addEventListener("click", (e) => {
    const item = (e.target as Element)?.closest<HTMLElement>(".dropdown-item");
    if (!item) return;
    const container = item.closest("[data-tags-container]");
    if (!container) return;
    e.preventDefault();
    const dropdown = item.closest<HTMLElement>(".dropdown-menu");
    if (dropdown) closeDropdown(dropdown);
    setActiveTag(container, item);
    switchTagPane(item);
  });

  // Dropdown toggle
  document.addEventListener("click", (e) => {
    const btn = (e.target as Element)?.closest<HTMLElement>(".dropdown-button");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const dropdown = btn.parentElement?.querySelector<HTMLElement>(".dropdown-menu");
    const isOpen =
      dropdown &&
      (dropdown.offsetParent !== null || dropdown.style.display === "block");

    closeAllDropdownsExcept(dropdown ?? null);
    if (!dropdown) return;

    if (!isOpen) {
      if (window.innerWidth <= 1220) {
        dropdown.setAttribute(
          "style",
          "display:block!important;visibility:visible!important;opacity:1!important;" +
            "position:fixed!important;top:20%!important;left:15px!important;right:15px!important;" +
            "bottom:auto!important;z-index:999999!important;background:#fff!important;" +
            "border:2px solid #2c5aa0!important;border-radius:8px!important;" +
            "box-shadow:0 8px 30px rgba(0,0,0,0.4)!important;max-height:60vh!important;" +
            "overflow-y:auto!important;width:auto!important;padding:5px!important;" +
            "transform:none!important;",
        );
      } else {
        const btnHeight = btn.offsetHeight;
        dropdown.setAttribute(
          "style",
          `display:block;position:absolute;top:0;left:0;` +
            `transform:translateY(${btnHeight + 2}px);z-index:1000;background:#fff;` +
            `border:1.5px solid rgba(0,0,0,0.176);border-radius:6px;` +
            `max-width:200px;max-height:500px;overflow-y:scroll;padding:8px 0;`,
        );
      }
      btn.setAttribute("aria-expanded", "true");
    } else {
      closeDropdown(dropdown);
    }
  });

  // Close dropdown on outside click
  document.addEventListener("click", (e) => {
    if (!(e.target as Element)?.closest(".dropdown")) {
      document.querySelectorAll<HTMLElement>(".dropdown-menu").forEach((dd) => {
        if (dd.offsetParent !== null || dd.style.display === "block") {
          closeDropdown(dd);
        }
      });
    }
  });

  // Prevent close when clicking inside dropdown
  document.addEventListener("click", (e) => {
    if ((e.target as Element)?.closest(".dropdown-menu")) {
      e.stopPropagation();
    }
  });

  // Close on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      document.querySelectorAll<HTMLElement>(".dropdown-menu").forEach((dd) => {
        if (dd.offsetParent !== null || dd.style.display === "block") {
          const btn = dd.parentElement?.querySelector<HTMLElement>(".dropdown-button");
          closeDropdown(dd);
          btn?.focus();
        }
      });
    }
  });
}

// Auto-init (matches original: runs at parse time inside IIFE, which is DOMContentLoaded equivalent for deferred script)
initTagWidget();
